export type TransactionMetadata = Record<string, unknown>;

type MetadataPaymentRequest = {
  description?: unknown;
  [key: string]: unknown;
};

const asMetadataPaymentRequest = (
  value: unknown
): MetadataPaymentRequest | undefined => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return undefined;
  }

  return value as MetadataPaymentRequest;
};

export const parseTransactionMetadata = (
  metadata: unknown
): TransactionMetadata => {
  if (!metadata) {
    return {};
  }

  if (typeof metadata === 'string') {
    try {
      const parsed = JSON.parse(metadata);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return parsed as TransactionMetadata;
      }
    } catch {
      return { payment_request: { description: metadata } };
    }
    return {};
  }

  if (typeof metadata === 'object' && !Array.isArray(metadata)) {
    return metadata as TransactionMetadata;
  }

  return {};
};

export const getMetadataString = (
  metadata: TransactionMetadata,
  key: string
): string | undefined => {
  const value = metadata[key];
  if (value === null || value === undefined || value === '') {
    return undefined;
  }
  return String(value);
};

export const getMetadataDescription = (
  metadata: TransactionMetadata
): string | undefined => {
  const topLevel = getMetadataString(metadata, 'description');
  if (topLevel) {
    return topLevel;
  }

  const paymentRequest = asMetadataPaymentRequest(metadata.payment_request);
  const nestedDescription = paymentRequest?.description;

  if (
    nestedDescription === null ||
    nestedDescription === undefined ||
    nestedDescription === ''
  ) {
    return undefined;
  }

  return String(nestedDescription);
};

export const getOrderItemIdFromMetadata = (
  metadata: TransactionMetadata
): string | undefined => {
  return (
    getMetadataString(metadata, 'order_item_id') ??
    getMetadataString(metadata, 'orderItemId') ??
    getMetadataString(metadata, 'order_itemId')
  );
};

export const formatMetadataValue = (value: unknown): string => {
  if (value === null || value === undefined || value === '') {
    return 'N/A';
  }

  if (typeof value === 'object') {
    return JSON.stringify(value, null, 2);
  }

  return String(value);
};

export const getMetadataEntries = (
  metadata: TransactionMetadata
): [string, unknown][] => {
  return Object.entries(metadata).sort(([a], [b]) => a.localeCompare(b));
};
